import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AlumniLayout from "@/components/AlumniLayout";
import AgentToolkit from "@/components/AgentToolkit";
import GroupsSection from "@/components/GroupsSection";
import { useBranding } from "@/lib/branding";
import { CalendarHeart, MapPin, Users, ArrowRight, Sparkles, Image as ImageIcon } from "lucide-react";

const HERO = "https://udallas.edu/_resources/images/_homepage_images/campus-students-discussing-on-mall.jpg";

const fmtDate = (s) => {
  if (!s) return "Date TBD";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function Experience() {
  const { user } = useAuth();
  const b = useBranding();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  // Class Agent tools are desktop-only — never mount them (or their fullscreen portal) on mobile.
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true));

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    api.get("/experience/events").then((r) => setEvents(r.data.events)).catch(() => {});
    api.get("/experience/classes").then((r) => setClasses(r.data.classes)).catch(() => {});
  }, []);

  const now = Date.now();
  const isPast = (e) => e.start && new Date(e.start).getTime() < now;
  const upcoming = events.filter((e) => !isPast(e)).slice(0, 3);
  const past = events.filter(isPast).sort((a, b) => (b.start || "").localeCompare(a.start || ""));
  const first = (user?.name || "friend").split(" ")[0];

  return (
    <AlumniLayout wide>
      {isDesktop && (user?.role === "alumnus" || user?.role === "class_agent") && (
        <AgentToolkit onNavigate={user?.role === "class_agent" ? (tab) => navigate(`/agent?tab=${tab}`) : undefined} />
      )}
      <div className="relative overflow-hidden rounded-xl border border-border shadow-sm mb-8">
        {b.hero_bg ? <img src={b.hero_bg} alt="Campus" className="absolute inset-0 h-full w-full object-cover" /> : null}
        <div className="absolute inset-0 bg-secondary/80" />
        <div className="relative z-10 px-7 py-6 text-white flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-accent"><Sparkles className="h-3.5 w-3.5" /> {b.university_name}</div>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2">Welcome back, {first}.</h1>
            <p className="text-white/80 mt-2 text-sm leading-relaxed">Reconnect with classmates, discover reunions and events, and relive the memories.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:flex-col lg:items-stretch shrink-0">
            <button data-testid="hero-directory" onClick={() => navigate("/experience/directory")} className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent text-secondary text-sm font-semibold hover:bg-accent/90 transition-colors"><Users className="h-4 w-4" /> Browse Class Directory</button>
            <button data-testid="hero-events" onClick={() => navigate("/experience/events")} className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"><CalendarHeart className="h-4 w-4" /> Reunions & Events</button>
          </div>
        </div>
      </div>

      <section className="mb-9">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2.5"><span className="inline-block h-6 w-1 rounded-full bg-accent" />Upcoming Reunions &amp; Events</h2>
          <button onClick={() => navigate("/experience/events")} className="text-sm text-primary hover:underline flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></button>
        </div>
        {upcoming.length === 0 && <div className="text-muted-foreground text-sm" data-testid="exp-no-events">No published events yet — check back soon.</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {upcoming.map((e) => (
            <button key={e.id} data-testid={`exp-event-${e.id}`} onClick={() => navigate(`/experience/events/${e.id}`)} className="relative text-left bg-card border border-border/70 rounded-xl overflow-hidden shadow-[0_2px_10px_rgba(0,26,75,0.06)] ring-1 ring-transparent p-4 pt-5 hover:shadow-[0_14px_36px_-8px_rgba(0,26,75,0.28)] hover:-translate-y-1 hover:ring-accent/40 hover:border-accent/50 transition-all duration-200 group">
              <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-accent to-accent/40" />
              <div className="flex items-center flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-primary">{e.category}</span>
                {e.my_rsvp && e.my_rsvp.status !== "invited" && <span className="inline-block text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">You: {e.my_rsvp.status}</span>}
              </div>
              <div className="font-serif text-lg tracking-tight mt-1.5 leading-snug">{e.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{fmtDate(e.start)}</div>
              {(e.location?.venue || e.location?.city) && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><MapPin className="h-3 w-3" />{[e.location?.venue, e.location?.city].filter(Boolean).join(", ")}</div>}
            </button>
          ))}
        </div>
      </section>

      {past.length > 0 && (
        <section className="mb-9" data-testid="exp-past-events">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2.5"><span className="inline-block h-6 w-1 rounded-full bg-slate-400" />Past Events &amp; Photos</h2>
            <button onClick={() => navigate("/experience/events")} className="text-sm text-primary hover:underline flex items-center gap-1">View all <ArrowRight className="h-3.5 w-3.5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {past.slice(0, 6).map((e) => (
              <button key={e.id} data-testid={`exp-past-event-${e.id}`} onClick={() => navigate(`/experience/events/${e.id}`)} className="relative text-left bg-card border border-border/70 rounded-xl overflow-hidden shadow-[0_2px_10px_rgba(0,26,75,0.06)] ring-1 ring-transparent p-4 pt-5 hover:shadow-[0_14px_36px_-8px_rgba(0,26,75,0.22)] hover:-translate-y-1 hover:ring-primary/25 hover:border-primary/40 transition-all duration-200 group">
                <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-slate-400/70 to-slate-300/30" />
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-muted text-muted-foreground">{e.category}</span>
                <div className="font-serif text-lg tracking-tight mt-1.5 leading-snug">{e.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{fmtDate(e.start)}</div>
                {(e.location?.venue || e.location?.city) && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><MapPin className="h-3 w-3" />{[e.location?.venue, e.location?.city].filter(Boolean).join(", ")}</div>}
                <div className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-primary"><ImageIcon className="h-3.5 w-3.5" /> {e.photo_count > 0 ? `${e.photo_count} photo${e.photo_count === 1 ? "" : "s"} — view gallery` : "Add photos"}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-serif text-2xl tracking-tight mb-4 flex items-center gap-2.5"><span className="inline-block h-6 w-1 rounded-full bg-accent" />Jump to a Class</h2>
        <div className="flex flex-wrap gap-2">
          {classes.slice(0, 24).map((c) => (
            <button key={c.class_year} data-testid={`exp-class-${c.class_year}`} onClick={() => navigate(`/experience/directory?year=${c.class_year}`)} className="px-3.5 py-2 rounded-full border border-border bg-card shadow-[0_1px_4px_rgba(0,26,75,0.05)] text-sm hover:border-accent hover:text-primary hover:shadow-[0_4px_12px_-2px_rgba(0,26,75,0.18)] hover:-translate-y-0.5 transition-all duration-200">
              ’{String(c.class_year).slice(-2)} <span className="text-muted-foreground">· {c.count}</span>
            </button>
          ))}
          {classes.length === 0 && <span className="text-muted-foreground text-sm">No classes yet.</span>}
        </div>
      </section>

      <GroupsSection />
    </AlumniLayout>
  );
}
